import type { TreatmentSupportRequest } from '@/types';

// ===== Pure links-view + ledger math (ported from mobile/src/screens/admin/linkFinancials.ts,
// see docs/superpowers/specs/2026-07-24-cases-c5-links-audit.md) =====

/** Web L207-213: net available for cases after the marketing share (VAT on marketing is refundable). */
export function calcLinkAvailable(received: number, x: number, y: number): number {
  const ms = (received * x) / ((x + y) || 1);
  return Math.floor(received - Math.ceil(ms));
}

export interface ComputedLinkCase {
  caseId: string; caseNumber?: number; nameAr: string; nameEn: string;
  idNumber: string; allocated: number; status: string;
}

export interface ComputedLink {
  id: string; platform: string; platformCaseNumber: string;
  url?: string; donorName?: string; treatmentCategoryId?: string; linkTitle?: string;
  actualReceived: number; marketingShare: number; refundableVAT: number;
  nonSaudiAllocated: number; caseInvoiceVAT: number; cashbackAmount: number;
  availableForCases: number; totalAllocated: number; remaining: number;
  addedAt?: Date; cases: ComputedLinkCase[];
}

export type CaseLite = Pick<TreatmentSupportRequest,
  'id' | 'caseNumber' | 'nameArabic' | 'nameEnglish' | 'idNumber' | 'idType' | 'status' | 'invoices' | 'donationEntries'
  | 'applicantName' | 'nationalId'>;

export interface LinkDocLite {
  id: string; platform: string; caseNumber: string;
  url?: string; donorName?: string; linkTitle?: string; treatmentCategoryId?: string; addedAt?: Date;
}

/** Audit §1 (web allLinks memo L329-428). actualReceived comes ONLY from donationSums. */
export function buildComputedLinks(
  links: LinkDocLite[],
  donationSums: Record<string, number>,
  cases: CaseLite[],
  providers: { id: string; cashbackPercentage?: number }[],
  x: number,
  y: number,
): ComputedLink[] {
  const grouped: Record<string, ComputedLink> = {};
  for (const l of links) {
    grouped[l.id] = {
      id: l.id, platform: l.platform, platformCaseNumber: l.caseNumber,
      url: l.url, donorName: l.donorName, treatmentCategoryId: l.treatmentCategoryId, linkTitle: l.linkTitle,
      actualReceived: donationSums[l.id] || 0,
      marketingShare: 0, refundableVAT: 0, nonSaudiAllocated: 0, caseInvoiceVAT: 0, cashbackAmount: 0,
      availableForCases: 0, totalAllocated: 0, remaining: 0, addedAt: l.addedAt, cases: [],
    };
  }
  for (const r of cases) {
    for (const entry of r.donationEntries || []) {
      const g = entry.linkId ? grouped[entry.linkId] : undefined;
      if (!g) continue;
      const amt = entry.amount || 0;
      g.totalAllocated += amt;
      if (r.idType !== 'national_id') g.nonSaudiAllocated += amt;
      const invoices = r.invoices || [];
      if (invoices.length > 0) {
        const invoicesTotal = invoices.reduce((s, inv) => s + (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0), 0);
        const invoiceVAT = invoices.reduce((s, inv) => s + (inv.taxAmount || 0), 0);
        if (invoicesTotal > 0 && invoiceVAT > 0) {
          g.caseInvoiceVAT += Math.ceil((invoiceVAT * amt) / invoicesTotal);
        }
        if (invoicesTotal > 0) {
          for (const inv of invoices) {
            const pct = providers.find((p) => p.id === inv.providerId)?.cashbackPercentage || 0;
            if (pct > 0) {
              const invAmt = (inv.totalAfterDiscount || 0) + (inv.taxAmount || 0);
              g.cashbackAmount += ((invAmt * pct) / 100) * (amt / invoicesTotal);
            }
          }
        }
      }
      g.cases.push({
        caseId: r.id, caseNumber: r.caseNumber,
        // Fallback to applicantName/nationalId when the primary fields are absent — mirrors
        // CoordinatorDashboard.tsx (read-only reference, not modified) so a case carrying only
        // the legacy fields still renders a name/id chip on the marketer surface.
        nameAr: r.nameArabic || r.applicantName || '', nameEn: r.nameEnglish || r.applicantName || '',
        idNumber: r.idNumber || r.nationalId || '', allocated: amt, status: r.status,
      });
    }
  }
  return Object.values(grouped).map((g) => {
    const ms = (g.actualReceived * x) / ((x + y) || 1);
    const marketingShare = Math.ceil(ms);
    const availableForCases = Math.floor(g.actualReceived - marketingShare);
    return {
      ...g,
      marketingShare,
      refundableVAT: Math.ceil(ms * 0.15) + g.caseInvoiceVAT,
      availableForCases,
      remaining: availableForCases - g.totalAllocated,
    };
  });
}

/** Audit §1 search (web L430-444). */
export function filterComputedLinks(list: ComputedLink[], query: string): ComputedLink[] {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter((l) =>
    l.platformCaseNumber.toLowerCase().includes(q) ||
    l.platform.toLowerCase().includes(q) ||
    (l.donorName || '').toLowerCase().includes(q) ||
    l.cases.some((c) =>
      String(c.caseNumber ?? '').includes(q) ||
      c.nameAr.toLowerCase().includes(q) ||
      c.nameEn.toLowerCase().includes(q) ||
      c.idNumber.toLowerCase().includes(q),
    ),
  );
}

/** Audit §1 sort (web L446-480): numeric columns, addedAt by time, default = platformCaseNumber localeCompare. */
export function sortComputedLinks(list: ComputedLink[], column: string, direction: 'asc' | 'desc'): ComputedLink[] {
  const dir = direction === 'asc' ? 1 : -1;
  const numeric: Record<string, (l: ComputedLink) => number> = {
    actualReceived: (l) => l.actualReceived,
    marketingShare: (l) => l.marketingShare,
    refundableVAT: (l) => l.refundableVAT,
    cashback: (l) => l.cashbackAmount,
    availableForCases: (l) => l.availableForCases,
    totalAllocated: (l) => l.totalAllocated,
    remaining: (l) => l.remaining,
    addedAt: (l) => (l.addedAt ? l.addedAt.getTime() : 0),
  };
  return [...list].sort((a, b) => {
    if (column === 'platform') return a.platform.localeCompare(b.platform) * dir;
    const f = numeric[column];
    if (f) return (f(a) - f(b)) * dir;
    return a.platformCaseNumber.localeCompare(b.platformCaseNumber) * dir;
  });
}

export interface LinksSummary {
  totalReceived: number; totalMarketing: number; totalNet: number;
  totalAllocated: number; totalRemaining: number; totalVAT: number; totalCashback: number;
}

/** Audit §1 summary cards (web L3815-3877); totalCashback is the only ceil'd grand total. */
export function linksSummary(list: ComputedLink[]): LinksSummary {
  return {
    totalReceived: list.reduce((s, l) => s + l.actualReceived, 0),
    totalMarketing: list.reduce((s, l) => s + l.marketingShare, 0),
    totalNet: list.reduce((s, l) => s + l.availableForCases, 0),
    totalAllocated: list.reduce((s, l) => s + l.totalAllocated, 0),
    totalRemaining: list.reduce((s, l) => s + l.remaining, 0),
    totalVAT: list.reduce((s, l) => s + l.refundableVAT, 0),
    totalCashback: Math.ceil(list.reduce((s, l) => s + l.cashbackAmount, 0)),
  };
}

export function pctOf(value: number, totalReceived: number): string {
  return totalReceived > 0 ? `${((value / totalReceived) * 100).toFixed(1)}%` : '—';
}

/** Audit §2 edit-link guard (web L650): reducing received must not undercut existing allocations. */
export function canReduceReceived(newReceived: number, totalAllocated: number, x: number, y: number): boolean {
  return calcLinkAvailable(newReceived, x, y) >= totalAllocated;
}

/** Deliberate fix of web bug (audit flagged issue 2): aggregate = importKey 'agg_*', never source==='manual' alone. */
export function findAggregateDonation<T extends { importKey?: string }>(linkDonations: T[]): T | undefined {
  return linkDonations.find((d) => d.importKey?.startsWith('agg_'));
}

/**
 * CRITICAL fix (marketer edit-project inflation bug): decides the single donation write, if any,
 * that saving the "received" field of the edit-project form should perform.
 *
 * The bug: the edit form prefills "received" from the COMPUTED total (sum of every donation on
 * the link — the aggregate row plus every itemised manual/auto-imported row), but the old save
 * handler wrote that number into the aggregate donation alone. Itemised donations were then
 * counted twice on every subsequent save, and if no aggregate row existed yet the handler created
 * one holding the full total, doubling everything.
 *
 * This function is the only place that decides the aggregate write. It never re-implements the
 * marketing split and it is never consulted at all when the link has any `source === 'jood'`
 * donation — callers must keep that lockout (checked before calling this) exactly as it exists
 * today.
 *
 * @param requestedReceived   The number the user has in the "received" field right now.
 * @param currentTotalReceived The computed total the field was prefilled with (== ComputedLink.actualReceived).
 * @param aggregateDonation   The link's current `agg_*` donation, if any (id + its current amount).
 * @param nonAggregateTotal   Sum of every OTHER donation on the link (itemised manual/auto rows) — excludes the aggregate row itself.
 *
 * Invariants:
 * 1. requestedReceived === currentTotalReceived  → { op: 'none' }            (field untouched → zero writes)
 * 2. after applying the returned op, nonAggregateTotal + newAggregateAmount === requestedReceived EXACTLY
 * 3. requestedReceived < nonAggregateTotal        → { op: 'refuse' }         (never a negative aggregate)
 * 4. not consulted at all when the link has a `source === 'jood'` donation (caller-enforced lockout)
 */
export type AggregateDonationWrite =
  | { op: 'none' }
  | { op: 'update'; amount: number }
  | { op: 'create'; amount: number }
  | { op: 'refuse' };

export function computeAggregateDonationWrite(
  requestedReceived: number,
  currentTotalReceived: number,
  aggregateDonation: { id: string; amount: number } | undefined,
  nonAggregateTotal: number,
): AggregateDonationWrite {
  if (requestedReceived === currentTotalReceived) return { op: 'none' };
  if (requestedReceived < nonAggregateTotal) return { op: 'refuse' };
  const amount = requestedReceived - nonAggregateTotal;
  if (aggregateDonation) return { op: 'update', amount };
  if (amount > 0) return { op: 'create', amount };
  return { op: 'none' };
}

/** Audit §3 (web L539-540): date inputs anchor to LOCAL NOON to dodge timezone date shifts. */
export function noonLocalDate(dateStr: string): Date {
  const [yr, mo, da] = dateStr.split('-').map(Number);
  return new Date(yr, mo - 1, da, 12, 0, 0);
}

/** Inverse companion: Date → 'YYYY-MM-DD' (local, zero-padded) for date TextInputs. */
export function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Display guard for marketer donation dates: an Invalid Date (dateless donation — the
 * donations.ts mapper now returns `new Date(NaN)` instead of defaulting to today, see
 * 2026-07-26-marketer-m3-monthly-report) must render as '—', not 'NaN-NaN-NaN'. Matches the
 * house pattern already used in CoordinatorDashboard.tsx / AdminHelpManagement.tsx.
 */
export function formatDonationDate(d: Date | undefined | null): string {
  if (!d || isNaN(d.getTime())) return '—';
  return toDateInput(d);
}

// ===== Monthly projects report (تقرير المشاريع الشهرية) — exact port of mobile
// monthlyProjectRows/filterYearRows (mobile/src/screens/admin/caseAdminTools.ts, Task 1); web
// needs its own year-shaping helpers since mobile's live in a mobile-only file. =====

// marketingCost is optional and only populated when monthlyProjectRows is called with x/y
// (marketing split settings) — existing callers that omit them keep getting the pre-existing
// shape exactly (undefined properties are invisible to toEqual), see linkFinancials.test.ts.
export interface MonthlyProjectRow { key: string; ts: number; count: number; total: number; marketingCost?: number }

/**
 * Fix 2 (controller ruling): the report shows money received FROM PROJECTS, and a deleted
 * project is no longer a project. Deleting a link deliberately leaves its `donations`
 * documents behind (AdminHelpManagement.tsx, read-only reference — "Donation transactions
 * deliberately NOT deleted"), so callers must run every donation list through this before
 * `monthlyProjectRows`, or orphaned donations keep inflating the report forever while the
 * projects screen (which groups by existing links) drops them immediately.
 */
export function filterDonationsToActiveLinks<T extends { linkId?: string }>(
  donations: T[],
  links: { id: string }[],
): T[] {
  const activeIds = new Set(links.map((l) => l.id));
  return donations.filter((d) => !!d.linkId && activeIds.has(d.linkId));
}

/**
 * Buckets donations by the date field's LOCAL year and month; skips missing/invalid date.
 *
 * `x`/`y` are the marketing-split settings (settings/treatmentMarketing) — optional so existing
 * callers (bucketing only, no marketing figure) are untouched. When both are provided, each
 * row's marketingCost is the split applied to that MONTH'S summed total (never per donation) —
 * same rounding as marketingStats' monthly breakdown above (Math.ceil, and the `(x + y) || 1`
 * divide-by-zero guard). Mobile port: mobile/src/screens/admin/caseAdminTools.ts monthlyProjectRows.
 */
export function monthlyProjectRows(
  donations: { date?: Date; amount?: number }[],
  x?: number,
  y?: number,
): MonthlyProjectRow[] {
  const map: Record<string, MonthlyProjectRow> = {};
  for (const d of donations) {
    const date = d.date;
    if (!date || Number.isNaN(date.getTime())) continue;
    const year = date.getFullYear();
    const month = date.getMonth();
    const key = `${year}-${String(month + 1).padStart(2, '0')}`;
    const row = (map[key] = map[key] || { key, ts: new Date(year, month, 1).getTime(), count: 0, total: 0 });
    row.count += 1;
    row.total += d.amount || 0;
  }
  const rows = Object.values(map);
  if (typeof x === 'number' && typeof y === 'number') {
    for (const r of rows) {
      r.marketingCost = monthlyMarketingCost(r.total, x, y);
    }
  }
  return rows.sort((a, b) => b.ts - a.ts);
}

/**
 * Single source of truth for the monthly-report marketing-cost split (Math.ceil, `(x+y)||1`
 * divide-by-zero guard). Extracted out of monthlyProjectRows so monthlyProjectRowsFromTotals
 * (fed by server-side per-month sum/count aggregates instead of a raw donations array) produces
 * IDENTICAL marketingCost figures for the same month total — one formula, two entry points.
 * Mobile port: mobile/src/screens/admin/caseAdminTools.ts monthlyMarketingCost.
 */
export function monthlyMarketingCost(total: number, x: number, y: number): number {
  return Math.ceil((total * x) / ((x + y) || 1));
}

/**
 * Aggregation-sourced counterpart to monthlyProjectRows: builds the same MonthlyProjectRow[]
 * shape directly from Firestore's per-month {total, count} aggregates (donations.ts
 * getMonthlyDonationTotals) instead of a downloaded donations array — the marketer monthly
 * report no longer needs to stream the whole donations collection to bucket it client-side.
 * `totals` keys are 'YYYY-MM' (LOCAL year/month), matching monthlyProjectRows' own key format.
 * Mobile port: mobile/src/screens/admin/caseAdminTools.ts monthlyProjectRowsFromTotals.
 */
export function monthlyProjectRowsFromTotals(
  totals: Record<string, { total: number; count: number }>,
  x: number,
  y: number,
): MonthlyProjectRow[] {
  return Object.entries(totals)
    .map(([key, { total, count }]) => {
      const [year, month] = key.split('-').map(Number);
      return {
        key,
        ts: new Date(year, month - 1, 1).getTime(),
        count,
        total,
        marketingCost: monthlyMarketingCost(total, x, y),
      };
    })
    .sort((a, b) => b.ts - a.ts);
}

/** Distinct years present in the rows, ascending. */
export function projectReportYears(rows: { key: string }[]): number[] {
  const years = new Set<number>();
  for (const r of rows) years.add(Number(r.key.split('-')[0]));
  return Array.from(years).sort((a, b) => a - b);
}

export interface FilterProjectYearResult {
  rows: { key: string; total: number; count: number; marketingCost: number }[];
  totalAmount: number;
  totalCount: number;
  totalMarketing: number;
}

/**
 * Always emits 12 months ascending, zero-filled for months with no donations. marketingCost
 * defaults to 0 for months with no data or when monthlyProjectRows was called without x/y.
 * Mobile port: mobile/src/screens/admin/caseAdminTools.ts filterProjectYearRows.
 */
export function filterProjectYearRows(rows: MonthlyProjectRow[], year: number): FilterProjectYearResult {
  const byKey: Record<string, MonthlyProjectRow> = {};
  for (const r of rows) byKey[r.key] = r;
  const months: { key: string; total: number; count: number; marketingCost: number }[] = [];
  let totalAmount = 0;
  let totalCount = 0;
  let totalMarketing = 0;
  for (let m = 0; m < 12; m++) {
    const key = `${year}-${String(m + 1).padStart(2, '0')}`;
    const r = byKey[key];
    const entry = { key, total: r?.total || 0, count: r?.count || 0, marketingCost: r?.marketingCost || 0 };
    months.push(entry);
    totalAmount += entry.total;
    totalCount += entry.count;
    totalMarketing += entry.marketingCost;
  }
  return { rows: months, totalAmount, totalCount, totalMarketing };
}
